import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InAppBrowser, InAppBrowserOptions } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { ApiService } from '@core/services';
import { IonContent, PickerController, Platform } from '@ionic/angular';
import { User } from '@shared/models';

import { AuthService } from '../services/auth.service';
import { DataService } from '../services/data.service';
import { HelpService } from '../services/help.service';
import { EnrollmentValidator } from '../validators/enrollment';
import { PasswordValidator } from '../validators/password';

@Component({
  selector: 'app-register',
  templateUrl: './register.page.html',
  styleUrls: ['./register.page.scss'],
})
export class RegisterPage implements OnInit {
  #apiService: ApiService = inject(ApiService);
  errors: string;
  driver = false;
  form: UntypedFormGroup;
  campus: string;
  gender: string;
  exatec = false;
  mitec$ = this.#apiService.get<boolean>('saml2/mitec');
  user: Partial<User> & { password?: string; password_confirmation?: string; exatec?: boolean } = {
    id: null,
    campus_registered: null,
    name: '',
    lastname: '',
    password: '',
    password_confirmation: '',
    gender: '',
    phone: '',
    email: '',
    birthday: '',
    enrollment: '',
    type: 'Estudiante',
    exatec: false,
  };
  type: string;
  iabOptions: InAppBrowserOptions = {
    location: 'no',
    hidenavigationbuttons: 'yes',
    toolbarposition: 'bottom',
    toolbarcolor: '#0e53a1',
    navigationbuttoncolor: '#ffffff',
    hideurlbar: 'yes',
    closebuttoncaption: 'Cerrar',
  };
  registerCampus: number;
  @ViewChild('content') private content!: IonContent;

  constructor(
    private auth: AuthService,
    private route: ActivatedRoute,
    private pickerCtrl: PickerController,
    private iab: InAppBrowser,
    private dataService: DataService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private platform: Platform,
    private helpService: HelpService
  ) {}

  ngOnInit() {
    let param = this.route.snapshot.paramMap.get('id');
    if (this.router.url.indexOf('settings') >= 0) {
      console.log('getUser');
      this.getUser();
    } else {
      const aux = param;
      if (param === 'Prepatec') {
        param = 'Estudiante';
      }
      this.user.type = param;
      this.typeChange(param);
      setTimeout(() => {
        if (aux === 'Prepatec') {
          this.form.controls['user_type_id'].setValue(1);
        }
        this.checkCampus();
      }, 100);
    }
  }

  checkCampus() {
    const campus = this.dataService.getData('registerCampus');
    if (campus) {
      this.registerCampus = campus;
      this.form.controls['campus_registered'].setValue(this.registerCampus);
    }
  }

  async getUser() {
    this.auth.getUser().then((user: User) => {
      this.user = { ...user };
      this.changeGender();
      this.typeChange('Edicion');
    });
  }

  refreshEmail(matricula) {
    // console.log("Value: ", this.form.controls.email.value);
    // console.log("Email: ", this.form.controls.email);
    if (this.user.type === 'Representativo' || this.user.type === 'Estudiante') {
      this.user.email = matricula + '@tec.mx';
      this.form.controls['email'].setValue(matricula + '@tec.mx');
      console.log(this.user.email);
    }
  }

  changeGender() {
    // console.log(this.user.gender);
    if (this.user.gender === 'male') {
      this.gender = 'Masculino';
    } else {
      this.gender = 'Femenino';
    }
  }

  typeChange(type) {
    console.log('Formulario para ' + type);
    switch (type) {
      case 'Estudiante':
        this.form = this.formBuilder.group({
          endpoint: 'students',
          user_type_id: 2,
          campus_registered: ['', Validators.required],
          name: [this.user.name, Validators.required],
          lastname: [this.user.lastname, Validators.required],
          email: [
            { value: this.user.email, disabled: true },
            [Validators.required, Validators.pattern('[A-Za-z0-9._%+-]{2,}@(itesm.mx|tec.mx)')],
          ],
          gender: [this.user.gender],
          type: [type, Validators.required],
          phone: [this.user.phone],
          password: [this.user.password, [Validators.required, Validators.minLength(6), PasswordValidator.strong]],
          password_confirmation: ['', [this.equalto('password')]],
          birthday: [this.user.birthday],
          enrollment: [
            { value: this.user.enrollment, disabled: this.user.id ? true : false },
            [Validators.required, EnrollmentValidator.check],
          ],
          exatec: [],
          termsAccepted: [false, Validators.requiredTrue],
        });
        break;
      case 'Colaborador':
        this.form = this.formBuilder.group({
          endpoint: 'employees',
          user_type_id: 4,
          campus_registered: ['', Validators.required],
          name: [this.user.name, Validators.required],
          lastname: [this.user.lastname, Validators.required],
          email: [
            this.user.email,
            [Validators.required, Validators.pattern('[A-Za-z0-9._%+-]{2,}@(itesm.mx|tec.mx|sorteostec.mx)')],
          ],
          gender: [this.user.gender],
          type: [type, Validators.required],
          phone: [this.user.phone],
          password: [this.user.password, [Validators.required, Validators.minLength(6), PasswordValidator.strong]],
          password_confirmation: ['', [this.equalto('password')]],
          birthday: [this.user.birthday],
          enrollment: [
            { value: this.user.enrollment, disabled: this.user.id ? true : false },
            [Validators.required, EnrollmentValidator.check],
          ],
          exatec: [],
          termsAccepted: [false, Validators.requiredTrue],
        });
        break;
      case 'Representativo':
        this.form = this.formBuilder.group({
          endpoint: 'representative',
          campus_registered: ['', Validators.required],
          name: [this.user.name],
          lastname: [this.user.lastname],
          email: [{ value: this.user.email, disabled: true }, Validators.required],
          gender: [this.user.gender],
          type: [type, Validators.required],
          phone: [this.user.phone],
          password: [this.user.password, [Validators.required, Validators.minLength(6), PasswordValidator.strong]],
          password_confirmation: ['', [this.equalto('password')]],
          birthday: [this.user.birthday],
          enrollment: [
            { value: this.user.enrollment, disabled: this.user.id ? true : false },
            [Validators.required, EnrollmentValidator.check],
          ],
          exatec: [],
          termsAccepted: [false, Validators.requiredTrue],
        });
        break;
      case 'Invitado':
        this.form = this.formBuilder.group({
          endpoint: 'guests',
          campus_registered: ['', Validators.required],
          name: [this.user.name, Validators.required],
          lastname: [this.user.lastname, Validators.required],
          email: [
            this.user.email,
            [Validators.required, Validators.pattern('^[A-Z0-9a-z._%±]+@([A-Za-z0-9-]+.)+[A-Za-z]{2,4}$')],
          ],
          gender: [this.user.gender],
          type: [type, Validators.required],
          phone: [this.user.phone, Validators.required],
          password: [this.user.password, [Validators.required, Validators.minLength(6), PasswordValidator.strong]],
          password_confirmation: ['', [this.equalto('password')]],
          birthday: [this.user.birthday],
          enrollment: [{ value: this.user.enrollment, disabled: this.user.id ? true : false }],
          exatec: [],
          termsAccepted: [false, Validators.requiredTrue],
        });
        break;
      case 'Edicion':
        this.form = this.formBuilder.group({
          endpoint: 'user',
          campus_registered: [this.user.campus_registered, Validators.required],
          name: [{ value: this.user.name, disabled: this.user.id ? true : false }, Validators.required],
          lastname: [{ value: this.user.lastname, disabled: this.user.id ? true : false }, Validators.required],
          email: [
            { value: this.user.email, disabled: true },
            [Validators.required, Validators.pattern('^[A-Z0-9a-z._%±]+@([A-Za-z0-9-]+.)+[A-Za-z]{2,4}$')],
          ],
          gender: [this.user.gender],
          type: [type],
          phone: [this.user.phone, Validators.required],
          password: [this.user.password, PasswordValidator.checkLength],
          password_confirmation: ['', [this.equalto('password')]],
          birthday: [this.user.birthday],
          enrollment: [{ value: this.user.enrollment, disabled: this.user.id ? true : false }],
          exatec: [],
          termsAccepted: [true],
        });
        this.form.controls['password_confirmation'].setValue(null);
        break;
    }
  }

  equalto(fieldName): ValidatorFn {
    return (
      control: AbstractControl
    ): {
      [key: string]: { isValid: boolean };
    } => {
      const input = control.value;
      if (typeof control.root.value[fieldName] === 'undefined') {
        return null;
      }
      const isValid = control.root.value[fieldName] === input;
      if (!isValid) {
        return { equalTo: { isValid } };
      } else {
        return null;
      }
    };
  }

  onTermsChecked($event) {
    if (!$event.detail.checked) {
      this.form.controls['termsAccepted'].setValue(false);
    } else {
      this.form.controls['termsAccepted'].setValue(true);
    }
  }

  async showCampusPicker() {
    if (this.user.type === 'Estudiante' || this.user.type === 'Colaborador') {
      const picker = await this.pickerCtrl.create({
        buttons: [
          {
            text: 'Cancelar',
            role: 'cancel',
          },
          {
            text: 'Seleccionar',
            handler: (data) => {
              this.campus = data.campus.text;
              this.form.controls['campus_registered'].setValue(data.campus.value);
            },
          },
        ],
        columns: [
          {
            name: 'campus',
            options: [
              { text: 'CCM', value: 3 },
              { text: 'CEM', value: 5 },
              { text: 'CEM - Esmeralda', value: 6 },
              { text: 'Guadalajara', value: 11 },
              { text: 'Querétaro', value: 9 },
              { text: 'Santa Fe', value: 10 },
            ],
          },
        ],
      });
      picker.present();
    } else {
      this.campus = 'CCM';
      this.form.controls['campus_registered'].setValue(3);
    }
  }

  async showGenderPicker() {
    const picker = await this.pickerCtrl.create({
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
        },
        {
          text: 'Seleccionar',
          handler: (data) => {
            this.gender = data.gender.text;
            this.form.controls['gender'].setValue(data.gender.value);
          },
        },
      ],
      columns: [
        {
          name: 'gender',
          options: [
            { text: 'Masculino', value: 'male' },
            { text: 'Femenino', value: 'female' },
          ],
        },
      ],
    });
    picker.columns[0].options.forEach((element) => {
      delete element.selected;
      delete element.duration;
      delete element.transform;
    });
    picker.present();
  }

  register() {
    console.log('Form: ', this.form.value);

    if (
      (this.driver && this.form.controls['pincode'].dirty && this.form.controls['pincode'].valid) ||
      this.form.valid
    ) {
      this.auth
        .register(this.form.value)
        .then((res) => {
          console.log(res);
          if (res) {
            if (typeof res === 'string') {
              this.errors = res;
            } else {
              this.errors = 'Datos incorrectos';
            }
            setTimeout(() => {
              this.content.scrollToBottom(300);
            }, 100);
          }
        })
        .catch((error) => {
          console.log('Error: ' + JSON.stringify(error));
        });
    }
  }

  update() {
    console.log('Update form: ', this.form.value);
    if (this.form.valid) {
      this.auth.update(this.form.value).then((res) => {
        if (res) {
          this.auth.presentToast('Datos actualizados correctamente');
          this.router.navigate(['/tecservice-transportec/profile']);
        }
      });
    }
  }

  goForgot() {}

  print() {
    console.log(this.form.value);
    console.log(this.form.controls['type'].value);
  }

  goTerms() {
    this.iab.create(
      (this.platform.is('ios') ? '' : '') +
        'https://tecservice.app/terminosycondiciones?campus=' +
        this.form.controls['campus_registered'].value,
      '_blank',
      this.iabOptions
    );
  }
}
